'use client';

import * as React from 'react';
import { Listbox } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ListOptions } from '../data/dataType';

type SelectProps = {
    className?: string;
    disabled?: boolean;
    name: string;
    onChange?: (value: string | number | null) => void;
    options: ListOptions[];
    placeholder?: string;
    defaultValue?: string | number | null;
};

const sanitizeOptions = (options: ListOptions[]): ListOptions[] =>
    options?.map(option => ({
        value: option.value === null ? '__null__' : option.value,
        label: option.label || '',
    }));

const Select: React.FC<SelectProps> = ({
    className,
    disabled = false,
    name,
    onChange,
    options,
    placeholder = 'Select an option',
    defaultValue,
}) => {
    const sanitizedOptions = React.useMemo(
        () => sanitizeOptions(options),
        [options],
    );

    const [selectedValue, setSelectedValue] = React.useState<
        string | number | null
    >(defaultValue === undefined ? '__null__' : defaultValue);

    const handleChange = (value: string | number) => {
        const actualValue: string | number | null =
            value === '__null__' ? null : value;
        setSelectedValue(value);

        if (onChange) {
            onChange(actualValue);
        }
    };

    const selectedLabel = React.useMemo(() => {
        if (sanitizedOptions && selectedValue) {
            const matchedOption = sanitizedOptions.find(
                option =>
                    option.value?.toString() === selectedValue?.toString(),
            );
            return matchedOption?.label || placeholder;
        }
    }, [selectedValue, sanitizedOptions, placeholder]);

    return (
        <Listbox
            disabled={disabled}
            name={name}
            onChange={handleChange}
            value={selectedValue}
        >
            <div className={cn('relative min-w-32', className)}>
                <Listbox.Button
                    className={cn(
                        'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm text-gray-900 text-left',
                        'focus:outline-none focus:ring-2 focus:ring-admin',
                    )}
                >
                    <span className="block truncate">{selectedLabel}</span>
                    <ChevronDown className="ml-2 h-5 w-5 text-gray-500" />
                </Listbox.Button>
                <Listbox.Options
                    className={cn(
                        'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg',
                        'ring-1 ring-black ring-opacity-5 focus:outline-none',
                    )}
                >
                    {sanitizedOptions?.map((option, index) => (
                        <Listbox.Option
                            className={({ active }) =>
                                cn(
                                    'relative cursor-pointer select-none py-2 pl-10 pr-4',
                                    active
                                        ? 'bg-admin text-white'
                                        : 'text-gray-900',
                                )
                            }
                            key={`${option.value?.toString()}-${index}`}
                            value={option.value}
                        >
                            {({ selected }) => (
                                <>
                                    <span
                                        className={cn(
                                            'block truncate',
                                            selected && 'font-medium',
                                        )}
                                    >
                                        {option.label}
                                    </span>
                                    {selected && (
                                        <span className="absolute left-2 top-2 flex items-center text-admin">
                                            <Check className="h-5 w-5" />
                                        </span>
                                    )}
                                </>
                            )}
                        </Listbox.Option>
                    ))}
                </Listbox.Options>
            </div>
        </Listbox>
    );
};

export default Select;
